import React, { useEffect } from 'react';
import { useZentorgaRouter } from './hooks/useZentorgaRouter';
import { useTemplateData } from './hooks/useTemplateData';
import { trackPageClick } from '../utils/api';
import { getTemplateComponent, checkLandingStatus } from './utils/templateMapper';
import { applyFooterLegalColor } from './utils/footerStyle';

// Template imports
import Template11 from './templates/Template11';
import Template12 from './templates/Template12';
import Template13 from './templates/Template13';
import Template21 from './templates/Template21';
import Template22 from './templates/Template22';
import Template23 from './templates/Template23';
import Template24 from './templates/Template24';
import Template25 from './templates/Template25';
import Template26 from './templates/Template26';
import Template31 from './templates/Template31';
import Template32 from './templates/Template32';
import Template33 from './templates/Template33';
import Template81 from './templates/Template81';
import Template14 from './templates/Template14';

import './styles/zentorga.scss';

/**
 * Zentorga 메인 라우터 컴포넌트
 * URL 패턴: /:adNumber/:code (예: /A001/XYZ123)
 */
const ZentorgaRouter = () => {
  const { adNumber, code, codeInfo, loading: codeLoading, error: codeError } = useZentorgaRouter();
  const { data: landingData, loading: dataLoading, error: dataError } = useTemplateData(adNumber);

  // 템플릿 컴포넌트 매핑
  const TEMPLATE_MAP = {
    'Template11': Template11,
    'Template12': Template12,
    'Template13': Template13,
    'Template14': Template14,
    'Template21': Template21,
    'Template22': Template22,
    'Template23': Template23,
    'Template24': Template24,
    'Template25': Template25,
    'Template26': Template26,
    'Template31': Template31,
    'Template32': Template32,
    'Template33': Template33,
    'Template81': Template81,
  };

  // 페이지 클릭 추적
  useEffect(() => {
    if (codeInfo?.id) {
      trackPageClick(codeInfo.id);
    }
  }, [codeInfo]);

  // API 데이터를 템플릿 형식으로 변환 (Hook은 조건문 이전에 호출)
  const transformedData = React.useMemo(() => {
    if (!landingData) return null;

    const transformed = { ...landingData };

    // surveyTopic과 surveyAnswer를 템플릿이 기대하는 형식으로 변환
    if (landingData.surveyTopic?.topic && landingData.surveyAnswer?.answers) {
      landingData.surveyTopic.topic.forEach((topic, index) => {
        const surveyNum = index + 1;
        transformed[`surveyTitle${surveyNum}`] = topic.text;

        const answers = landingData.surveyAnswer.answers[index] || [];
        answers.forEach((answer, answerIndex) => {
          transformed[`surveyOpt${surveyNum}_${answerIndex + 1}`] = answer;
        });
      });
    }

    // 푸터 Copyright·사업자등록번호 줄 글자색 커스텀 (대상 랜딩만)
    transformed.footerDetail = applyFooterLegalColor(transformed.footerDetail, landingData.adNumber || adNumber, landingData.footerLegalColor);

    return transformed;
  }, [landingData, adNumber]);

  // 신청버튼 띄움형(fixed) 처리
  // 버튼이 position:fixed 로 화면 하단에 겹쳐 뜨므로 마지막 콘텐츠가 가린다.
  // body 에 클래스를 걸어 레이아웃 하단 여백을 확보한다(스타일은 zentorga.scss).
  // SPA 라 다른 랜딩으로 이동하면 반드시 제거해야 한다.
  useEffect(() => {
    if (transformedData?.postBtnLocate !== 'fixed') return undefined;

    document.body.classList.add('has-fixed-postbtn');
    return () => document.body.classList.remove('has-fixed-postbtn');
  }, [transformedData?.postBtnLocate]);

  // 로딩 상태
  if (codeLoading || dataLoading) {
    return (
      <div className="zentorga-layout" style={{ padding: '50px', textAlign: 'center' }}>
        <p>로딩 중...</p>
      </div>
    );
  }

  // 에러 상태
  if (codeError || dataError) {
    return (
      <div className="zentorga-layout" style={{ padding: '50px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>페이지를 찾을 수 없습니다.</p>
        <p style={{ fontSize: '14px', color: '#666' }}>
          URL을 확인하거나 관리자에게 문의해주세요.
        </p>
      </div>
    );
  }

  // 랜딩 데이터 검증
  const landingStatus = checkLandingStatus(transformedData);

  if (!landingStatus.isActive) {
    return (
      <div className="zentorga-layout" style={{ padding: '50px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>{landingStatus.message}</p>
      </div>
    );
  }

  // 템플릿 선택
  const templateName = getTemplateComponent(transformedData.templateKey);
  const TemplateComponent = TEMPLATE_MAP[templateName];

  // 템플릿이 아직 구현되지 않은 경우 Template11 사용
  if (!TemplateComponent) {
    return (
      <Template11
        landingData={transformedData}
        urlCode={code}
        urlCodeSettingId={codeInfo?.id}
      />
    );
  }

  return (
    <TemplateComponent
      landingData={transformedData}
      urlCode={code}
      urlCodeSettingId={codeInfo?.id}
      adNumber={adNumber}
    />
  );
};

export default ZentorgaRouter;
